import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, CheckCircle, ArrowRight, MessageCircle, Sparkles, HelpCircle, Headphones, Youtube, Facebook, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

// TikTok icon component (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Social media links
const SOCIAL_LINKS = {
  youtube: 'https://youtube.com/@mnaconsultinginc?si=5o67ypoi0e_9DMm7',
  facebook: 'https://www.facebook.com/share/1BgtoWRbn4/',
  instagram: 'https://www.instagram.com/mna_consulting?igsh=aXFvNHVtbTRubjlp',
  tiktok: 'https://www.tiktok.com/@mnaconsult?_r=1&_t=ZS-95qT6u8KN3U'
};

// Helper to get UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source') || params.get('fbclid') ? 'facebook' : 'direct',
    utm_campaign: params.get('utm_campaign') || '',
    utm_medium: params.get('utm_medium') || ''
  };
};

const LandingPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [utmData, setUtmData] = useState({ source: 'direct', utm_campaign: '', utm_medium: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('12269297350');

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpForm, setHelpForm] = useState({ name: '', email: '', message: '' });
  const [isSubmittingHelp, setIsSubmittingHelp] = useState(false);

  const handleHelpSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingHelp(true);
    
    try {
      // Send form to backend (emails to info@mnaconsult.com)
      await axios.post(`${API}/contact`, {
        name: helpForm.name,
        email: helpForm.email,
        message: helpForm.message
      });
      
      // Also open WhatsApp with the message
      const message = encodeURIComponent(`Customer Service Request:\n\nName: ${helpForm.name}\nEmail: ${helpForm.email}\nMessage: ${helpForm.message}`);
      window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
      
      toast.success('Message sent! Also opening WhatsApp...');
      setShowHelpModal(false);
      setHelpForm({ name: '', email: '', message: '' });
    } catch (error) {
      // Even if email fails, still open WhatsApp
      const message = encodeURIComponent(`Customer Service Request:\n\nName: ${helpForm.name}\nEmail: ${helpForm.email}\nMessage: ${helpForm.message}`);
      window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
      toast.success('Opening WhatsApp...');
      setShowHelpModal(false);
      setHelpForm({ name: '', email: '', message: '' });
    } finally {
      setIsSubmittingHelp(false);
    }
  };

  useEffect(() => {
    // Capture UTM parameters on page load
    const params = getUtmParams();
    
    // Check for platform-specific click IDs
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('fbclid')) {
      params.source = 'facebook';
    } else if (urlParams.get('gclid')) {
      params.source = 'google';
    } else if (urlParams.get('ttclid')) {
      params.source = 'tiktok';
    } else if (urlParams.get('li_fat_id')) {
      params.source = 'linkedin';
    } else if (params.source === 'direct' && urlParams.get('utm_source')) {
      params.source = urlParams.get('utm_source').toLowerCase();
    }
    
    setUtmData(params);
    
    // Check if we need to scroll to a section (e.g., from affiliates page)
    const scrollTo = urlParams.get('scrollTo');
    if (scrollTo === 'get-started') {
      setTimeout(() => {
        const element = document.getElementById('get-started');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
    
    // Fetch WhatsApp number from content settings
    const fetchWhatsApp = async () => {
      try {
        const response = await axios.get(`${API}/content/settings`);
        if (response.data?.whatsapp_number) {
          setWhatsappNumber(response.data.whatsapp_number);
        }
      } catch (error) {
        // Use default number if not set
      }
    };
    fetchWhatsApp();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, { ...formData, ...utmData });
      setSubmitted(true);
      toast.success('Thank you! Check your email for the next steps.');
      
      // Track Lead event in Facebook Pixel
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Coaching Lead Form',
          content_category: 'Lead Capture'
        });
      }
      
      setFormData({ name: '', email: '', phone: '' });
      
      // Redirect to booking page after 3 seconds
      setTimeout(() => {
        window.location.href = '/book?type=first_coaching_call';
      }, 3000);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      {/* Glass Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl backdrop-saturate-150 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-[#FF4500]" />
              <h1 className="text-xl md:text-2xl font-black text-[#0A0A0A] tracking-tight">MNA Consult</h1>
            </div>
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-4">
              <a href="#opportunity" className="text-[#525252] hover:text-[#FF4500] transition-colors duration-300">The Opportunity</a>
              <a href="#how-it-works" className="text-[#525252] hover:text-[#FF4500] transition-colors duration-300">How It Works</a>
              <button 
                onClick={() => setShowHelpModal(true)}
                className="flex items-center gap-1 text-[#525252] hover:text-[#FF4500] transition-colors duration-300"
                data-testid="header-help-button"
              >
                <HelpCircle className="w-4 h-4" />
                Help
              </button>
              <a href="/book" className="bg-[#FF4500] text-white px-5 py-2 rounded-lg hover:bg-[#E03E00] transition-all duration-300 shadow-[0_8px_16px_rgba(255,69,0,0.3)]" data-testid="header-book-call-button">Book First Inquiry</a>
              <a href="/book?type=next_steps_call" className="bg-[#9C27B0] text-white px-5 py-2 rounded-lg hover:bg-[#7B1FA2] transition-all duration-300 shadow-[0_8px_16px_rgba(156,39,176,0.3)]" data-testid="header-next-steps-button">Book Next Steps Call</a>
            </nav>
            {/* Mobile Navigation */}
            <div className="flex md:hidden items-center gap-2">
              <button 
                onClick={() => setShowHelpModal(true)}
                className="text-[#525252] p-1.5"
                data-testid="mobile-help-button"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              <a href="/book" className="bg-[#FF4500] text-white px-3 py-1.5 rounded-lg text-xs font-semibold">First Inquiry</a>
              <a href="/book?type=next_steps_call" className="bg-[#9C27B0] text-white px-3 py-1.5 rounded-lg text-xs font-semibold">Next Steps Call</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner Image */}
      <section className="relative w-full">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-4xl mx-auto px-4 pt-8"
        >
          <img 
            src="/hero-image.png" 
            alt="Are you earning while you scroll?" 
            className="w-full h-auto rounded-2xl shadow-2xl"
          />
        </motion.div>
      </section>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32" style={{
        background: 'linear-gradient(135deg, rgba(255,69,0,0.05) 0%, rgba(0,85,255,0.05) 100%)'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block bg-[#FFD60A]/20 px-4 py-2 rounded-full mb-6">
                <p className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-widest">Digital Marketing Opportunity</p>
              </div>
              <h1 className="text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6">
                Build Your <span className="text-[#FF4500]">Digital Marketing</span> Business Today
              </h1>
              <p className="text-lg text-[#525252] leading-relaxed mb-8">
                Discover a proven business model that's helping entrepreneurs worldwide create successful digital marketing businesses. Watch our FREE 20-minute overview and book your personalized inquiry call.
              </p>
              
              {/* Benefits */}
              <div className="space-y-4 mb-8">
                {[
                  'Complete training and resources',
                  'Ongoing coaching and support',
                  'Proven business systems',
                  'Join a community of entrepreneurs'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-[#0055FF] flex-shrink-0" />
                    <p className="text-base text-[#0A0A0A]">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="#register" className="inline-flex items-center gap-2 bg-[#FF4500] text-white px-8 py-4 rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold shadow-[0_8px_16px_rgba(255,69,0,0.3)] hover:translate-y-[-2px]" data-testid="hero-get-started-button">
                  Get Started Free
                  <ArrowRight className="w-5 h-5" />
                </a>
                <button onClick={handleWhatsApp} className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg hover:bg-[#1EBE5A] transition-all duration-300 font-semibold shadow-[0_8px_16px_rgba(37,211,102,0.3)]" data-testid="whatsapp-button">
                  <MessageCircle className="w-5 h-5" />
                  Chat on WhatsApp
                </button>
              </div>
            </motion.div>

            {/* Right Column - Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.pexels.com/photos/3184611/pexels-photo-3184611.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                  alt="Digital marketing opportunity" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works - Bento Grid */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4">How It Works</h2>
            <p className="text-lg text-[#525252] max-w-2xl mx-auto">Three simple steps to start your digital marketing business journey</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Watch FREE Video',
                description: 'Get instant access to our 20-minute overview explaining the complete business model',
                icon: '🎬'
              },
              {
                step: '02',
                title: 'Book Your Call',
                description: 'Schedule a FREE 30-minute inquiry call to discuss your goals and next steps',
                icon: '📞'
              },
              {
                step: '03',
                title: 'Get Started',
                description: 'Join the program and access all training, resources, and ongoing support',
                icon: '🚀'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-8 hover:shadow-xl hover:translate-y-[-2px] transition-all duration-300"
              >
                <div className="text-6xl mb-4">{item.icon}</div>
                <div className="text-sm font-semibold text-[#525252] uppercase tracking-widest mb-2">Step {item.step}</div>
                <h3 className="text-xl lg:text-2xl font-semibold tracking-tight mb-3">{item.title}</h3>
                <p className="text-base text-[#525252] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture Form */}
      <section id="register" className="py-20" style={{
        background: 'linear-gradient(135deg, rgba(0,85,255,0.05) 0%, rgba(255,69,0,0.05) 100%)'
      }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            id="get-started"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 border border-[#E5E5E5]"
          >
            {!submitted ? (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4">Get Instant Access</h2>
                  <p className="text-lg text-[#525252]">Start your journey today - watch the FREE video and book your inquiry call</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-capture-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#0A0A0A] mb-2">Full Name</label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] transition-all duration-300"
                      placeholder="Enter your full name"
                      data-testid="name-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#0A0A0A] mb-2">Email Address</label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] transition-all duration-300"
                      placeholder="Enter your email"
                      data-testid="email-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#0A0A0A] mb-2">Phone Number</label>
                    <input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] transition-all duration-300"
                      placeholder="Enter your phone number"
                      data-testid="phone-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FF4500] text-white h-12 rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold shadow-[0_8px_16px_rgba(255,69,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-lead-form"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get FREE Access Now'}
                  </button>

                  <p className="text-sm text-[#525252] text-center">No credit card required. Instant access to the video.</p>
                </form>
              </>
            ) : (
              <div className="text-center py-12" data-testid="success-message">
                <CheckCircle className="w-16 h-16 text-[#0055FF] mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Check Your Email!</h3>
                <p className="text-lg text-[#525252] mb-6">We've sent you the video link. Redirecting you to book your call...</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-6 h-6 text-[#FF4500]" />
                <h3 className="text-xl font-bold">MNA Consult</h3>
              </div>
              <p className="text-[#999]">Building successful digital marketing businesses worldwide.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-[#999]">
                  <Mail className="w-4 h-4" />
                  info@mnaconsult.com
                </p>
                <p className="flex items-center gap-2 text-[#999]">
                  <Phone className="w-4 h-4" />
                  226-929-7350
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <a href="/book" className="block text-[#999] hover:text-[#FF4500] transition-colors duration-300">Book First Inquiry</a>
                <a href="/book?type=next_steps_call" className="block text-[#999] hover:text-[#FF4500] transition-colors duration-300">Book Next Steps Call</a>
                <a href="https://www.mnaconsult.com/policy" target="_blank" rel="noopener noreferrer" className="block text-[#999] hover:text-[#FF4500] transition-colors duration-300">Privacy Policy</a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Customer Service</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => setShowHelpModal(true)}
                  className="flex items-center gap-2 text-[#999] hover:text-[#25D366] transition-colors duration-300"
                >
                  <Headphones className="w-4 h-4" />
                  Contact Support
                </button>
                <button 
                  onClick={handleWhatsApp}
                  className="flex items-center gap-2 text-[#999] hover:text-[#25D366] transition-colors duration-300"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp Us
                </button>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Follow Us</h4>
              <div className="flex items-center gap-4">
                <a 
                  href={SOCIAL_LINKS.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#333] rounded-full flex items-center justify-center text-[#999] hover:bg-[#FF0000] hover:text-white transition-all duration-300"
                  aria-label="YouTube"
                  data-testid="social-youtube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
                <a 
                  href={SOCIAL_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#333] rounded-full flex items-center justify-center text-[#999] hover:bg-[#1877F2] hover:text-white transition-all duration-300"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href={SOCIAL_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#333] rounded-full flex items-center justify-center text-[#999] hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white transition-all duration-300"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href={SOCIAL_LINKS.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#333] rounded-full flex items-center justify-center text-[#999] hover:bg-[#000000] hover:text-white transition-all duration-300"
                  aria-label="TikTok"
                  data-testid="social-tiktok"
                >
                  <TikTokIcon className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-[#333] mt-8 pt-8 text-center text-[#666]">
            <p>&copy; 2025 MNA Consult. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 bg-[#25D366] text-white px-4 py-3 rounded-full shadow-lg hover:bg-[#1EBE5A] transition-all duration-300 flex items-center gap-2 hover:scale-105 z-50"
        data-testid="floating-whatsapp-button"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-semibold hidden sm:inline">Need Help?</span>
      </button>

      {/* Help/Customer Service Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Headphones className="w-6 h-6 text-[#FF4500]" />
                <h2 className="text-xl font-bold">Customer Service</h2>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                &times;
              </button>
            </div>

            {/* Quick Contact Options */}
            <div className="space-y-3 mb-6">
              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center gap-3 bg-[#25D366] text-white px-4 py-3 rounded-lg hover:bg-[#1EBE5A] transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">Chat on WhatsApp</p>
                  <p className="text-xs opacity-90">Get instant support</p>
                </div>
              </button>
            </div>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or send us a message</span>
              </div>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleHelpSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                <input
                  type="text"
                  value={helpForm.name}
                  onChange={(e) => setHelpForm({...helpForm, name: e.target.value})}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={helpForm.email}
                  onChange={(e) => setHelpForm({...helpForm, email: e.target.value})}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Enter your email"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">How can we help?</label>
                <textarea
                  value={helpForm.message}
                  onChange={(e) => setHelpForm({...helpForm, message: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Describe your question, concern, or feedback..."
                  rows={4}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmittingHelp}
                className="w-full bg-[#FF4500] text-white py-3 rounded-lg hover:bg-[#E03E00] transition-all font-semibold disabled:opacity-50"
              >
                {isSubmittingHelp ? 'Sending...' : 'Send via WhatsApp'}
              </button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              We typically respond within 24 hours
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;